import type { TranslationFn } from "@/utils/countries";
import type { IconName, IconProp } from "@fortawesome/fontawesome-svg-core";
import { getRegistryLabel } from "@/utils/registries";
import { OrderType } from "../common/companyOrderTypes";
import {
  RegistryEventType,
  type EventRowType,
  type RegistryEventConnection,
} from "./transactionLogTypes";

const ORDER_TYPE_LABEL_KEYS: Record<OrderType, string> = {
  [OrderType.SEND]: "common.actions.send",
  [OrderType.RECEIVE]: "common.actions.receive",
  [OrderType.BUY]: "common.actions.buy",
  [OrderType.SELL]: "common.actions.sell",
  [OrderType.CANCEL]: "common.actions.cancel",
  [OrderType.BUY_AND_CANCEL]: "common.actions.buy_and_cancel",
};

const ORDER_TYPE_ICONS: Record<OrderType, IconName> = {
  [OrderType.SEND]: "paper-plane",
  [OrderType.RECEIVE]: "inbox",
  [OrderType.BUY]: "cart-shopping",
  [OrderType.SELL]: "dollar-sign",
  [OrderType.CANCEL]: "ban",
  [OrderType.BUY_AND_CANCEL]: "layer-group",
};

const EVENT_TYPE_ICONS: Record<EventRowType, IconName> = {
  [RegistryEventType.ISSUANCE]: "certificate",
  [RegistryEventType.INCOMING_TRANSFER]: "arrow-left",
  [RegistryEventType.OUTGOING_TRANSFER]: "arrow-right",
  [RegistryEventType.CANCEL]: "ban",
  [RegistryEventType.EXPIRY]: "hourglass-half",
  [RegistryEventType.REVOCATION]: "rotate-left",
  ALLOCATION: "layer-group",
  RETURN: "layer-group",
};

export const formatOrderType = (type: OrderType, t: TranslationFn): string =>
  ORDER_TYPE_LABEL_KEYS[type] ? t(ORDER_TYPE_LABEL_KEYS[type]) : type;

export const orderTypeIcon = (type: OrderType): IconProp => [
  "far",
  ORDER_TYPE_ICONS[type] ?? "list-ul",
];

/** Table label of a single event row, e.g. "Registry transfer in" or "Portfolio allocation". */
export const formatEventRowType = (
  type: EventRowType,
  t: TranslationFn
): string => t(`company_orders.transactions.event_row_types.${type}`);

/** Table label of an event group row, e.g. "Registry issuances · 23". */
export const formatEventGroupRowType = (
  type: EventRowType,
  count: number,
  t: TranslationFn
): string =>
  t("company_orders.transactions.event_group_label", {
    label: t(`company_orders.transactions.event_group_types.${type}`),
    count,
  });

/** Plain event type name used inside the modals, e.g. "Incoming transfer". */
export const formatEventType = (
  type: RegistryEventType,
  t: TranslationFn
): string => t(`company_orders.transactions.event_types.${type}`);

export const eventTypeIcon = (type: EventRowType): IconProp => [
  "far",
  EVENT_TYPE_ICONS[type] ?? "list-ul",
];

/** The connection an event was read from, e.g. "Latvia (AST) · 25XP98YF45". */
export const formatConnection = (
  connection: RegistryEventConnection,
  t: TranslationFn
): string =>
  [getRegistryLabel(connection.region, t), connection.registry_eecs]
    .filter(Boolean)
    .join(" · ");
